import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getTruckRoute } from "@/lib/mapbox";
import {
  buildDirectLoadPayload,
  buildDirectStopRows,
  geocodeCity,
  type DirectBulkParseResult,
  type DirectBulkRow,
  type DirectSendTo,
  type GeoPoint,
} from "@/lib/lrDirect/bulkDirectUploadSchema";

/**
 * LR Direct — bulk post validated CSV rows into `direct_loads`.
 *
 * Per-row it mirrors useInstantPostFromTemplate: silent-write guard on the
 * insert, stops pair, then the same broadcast fan-out. Writes to direct_loads
 * ONLY — never the LaneRunner `loads` table.
 */

const ROW_TIMEOUT_MS = 20_000;

export interface BulkRowOutcome {
  rowNumber: number;
  lane: string;
  ok: boolean;
  error?: string;
  loadId?: string;
  postType?: "load" | "quote";
}

export interface BulkPostSummary {
  posted: number;
  failed: number;
  skipped: number;
  cancelled: number;
  total: number;
  rows: BulkRowOutcome[];
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Row timed out after ${ms / 1000}s`)), ms),
    ),
  ]);
}

const geoKey = (city: string, state: string) => `${city.trim().toLowerCase()}|${state.trim().toUpperCase()}`;

function laneOf(row: DirectBulkRow) {
  const d = row.data;
  return `${d.origin_city}, ${d.origin_state} → ${d.destination_city}, ${d.destination_state}`;
}

export function useBulkPostDirectLoads() {
  const [posting, setPosting] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [summary, setSummary] = useState<BulkPostSummary | null>(null);
  const cancelRef = useRef(false);

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const reset = useCallback(() => {
    setSummary(null);
    setProgress(null);
    cancelRef.current = false;
  }, []);

  const post = useCallback(
    async (args: {
      parsed: DirectBulkParseResult;
      userId: string;
      sendTo: DirectSendTo;
      postType: "load" | "quote";
      privateNetworkId: string | null;
      isDemo?: boolean;
    }): Promise<BulkPostSummary> => {
      const { parsed, userId, sendTo, postType, privateNetworkId, isDemo } = args;
      const isPrivate = sendTo === "private" || sendTo === "both";
      setPosting(true);
      setSummary(null);
      cancelRef.current = false;
      setProgress({ current: 0, total: parsed.validRows.length });

      // Company (optional on direct_loads, best effort)
      let companyId: string | null = null;
      try {
        const { data } = await supabase.rpc("get_user_companies", { user_uuid: userId });
        const ids = data as string[] | null;
        if (ids && ids.length > 0) companyId = ids[0];
      } catch {
        /* optional */
      }

      // Private member count → max_bidders (every member must be able to bid)
      let networkMembers = 0;
      if (privateNetworkId) {
        const { count } = await supabase
          .from("shipper_network_brokers" as any)
          .select("id", { count: "exact", head: true })
          .eq("network_id", privateNetworkId)
          .eq("status", "accepted")
          .is("removed_at", null);
        networkMembers = count ?? 0;
      }

      /* --- Geocode dedup across the whole batch --- */
      const keys = new Set<string>();
      for (const row of parsed.validRows) {
        keys.add(geoKey(row.data.origin_city, row.data.origin_state));
        keys.add(geoKey(row.data.destination_city, row.data.destination_state));
      }
      const geoCache = new Map<string, GeoPoint | null>();
      for (const key of keys) {
        if (cancelRef.current) break;
        const [city, state] = key.split("|");
        try {
          const geo = await geocodeCity(city, state);
          geoCache.set(key, geo ? { city: geo.city, state: geo.state, lat: geo.lat, lng: geo.lng } : null);
        } catch {
          geoCache.set(key, null);
        }
      }

      /* --- Row-by-row posting --- */
      const outcomes: BulkRowOutcome[] = [];
      const postedLoadIds: { loadId: string; postType: "load" | "quote"; sendTo: DirectSendTo }[] = [];
      let posted = 0;
      let failed = 0;
      let cancelled = 0;

      for (let i = 0; i < parsed.validRows.length; i++) {
        if (cancelRef.current) {
          cancelled = parsed.validRows.length - i;
          break;
        }
        const row = parsed.validRows[i];
        setProgress({ current: i + 1, total: parsed.validRows.length });

        const fail = (error: string) => {
          outcomes.push({ rowNumber: row.rowNumber, lane: laneOf(row), ok: false, error });
          failed++;
        };

        try {
          await withTimeout(
            (async () => {
              const origin = geoCache.get(geoKey(row.data.origin_city, row.data.origin_state)) ?? null;
              if (!origin) {
                fail(`Could not locate origin "${row.data.origin_city}, ${row.data.origin_state}"`);
                return;
              }
              const destination =
                geoCache.get(geoKey(row.data.destination_city, row.data.destination_state)) ?? null;
              if (!destination) {
                fail(`Could not locate destination "${row.data.destination_city}, ${row.data.destination_state}"`);
                return;
              }

              // Best-effort mileage — never blocks the post
              let totalMiles: number | null = null;
              try {
                const r = await getTruckRoute([origin.lng, origin.lat], [destination.lng, destination.lat]);
                if (r?.distance) totalMiles = Math.round(r.distance);
              } catch {
                /* non-fatal */
              }

              const payload = buildDirectLoadPayload({
                row,
                origin,
                destination,
                userId,
                companyId,
                sendTo,
                postType,
                privateNetworkId,
                maxBidders: isPrivate ? Math.max(networkMembers, 5, 1) : 5,
                totalMiles,
                isDemo: isDemo === true,
              });

              // Silent-write guard: a returned id is the only proof it landed.
              const { data: newLoad, error } = await supabase
                .from("direct_loads" as any)
                .insert(payload)
                .select("id")
                .single();
              if (error) {
                fail(error.message);
                return;
              }
              const loadId = (newLoad as any)?.id as string | undefined;
              if (!loadId) {
                fail("The post was not saved (no row returned)");
                return;
              }

              const { data: insertedStops, error: stopsErr } = await supabase
                .from("direct_load_stops" as any)
                .insert(buildDirectStopRows(loadId, row, origin, destination))
                .select("id");
              // Never swallow: a load whose stops failed to save is a failed
              // row — surface it in the summary instead of reporting success.
              if (stopsErr) {
                fail(`Load posted but stops failed to save: ${stopsErr.message}`);
                return;
              }
              if ((insertedStops?.length ?? 0) !== 2) {
                fail("Load posted but stops did not save completely. Check the load's stop list.");
                return;
              }

              // Notifications are NOT fired per lane on the bulk path. Ids are
              // collected and a single aggregated digest per recipient is sent
              // after the batch finishes (see broadcast-bulk-load-digest).
              postedLoadIds.push({ loadId, postType, sendTo });

              outcomes.push({ rowNumber: row.rowNumber, lane: laneOf(row), ok: true, loadId, postType });
              posted++;
            })(),
            ROW_TIMEOUT_MS,
          );
        } catch (err: any) {
          fail(err?.message || "Unknown error");
        }
      }

      /* --- ONE aggregated digest per recipient for the whole batch --- */
      if (postedLoadIds.length === 1) {
        // Single lane: keep the normal per-load notification behavior.
        const only = postedLoadIds[0];
        const isPrivate = only.sendTo === "private" || only.sendTo === "both";
        try {
          if (only.postType === "quote") {
            await supabase.functions.invoke("broadcast-new-quote-request", { body: { direct_load_id: only.loadId } });
          } else {
            if (isPrivate) {
              await supabase.functions.invoke("broadcast-new-private-load", { body: { direct_load_id: only.loadId } });
            }
            if (only.sendTo === "open" || only.sendTo === "both") {
              await supabase.functions.invoke("broadcast-new-open-load", { body: { direct_load_id: only.loadId } });
            }
          }
        } catch (e) {
          console.warn("[bulkDirect] single-lane broadcast failed", e);
        }
      } else if (postedLoadIds.length > 1) {
        try {
          const { error: digestErr } = await supabase.functions.invoke("broadcast-bulk-load-digest", {
            body: { direct_load_ids: postedLoadIds.map((p) => p.loadId) },
          });
          if (digestErr) console.warn("[bulkDirect] digest notification failed", digestErr);
        } catch (e) {
          console.warn("[bulkDirect] digest notification failed", e);
        }
      }

      const result: BulkPostSummary = {
        posted,
        failed,
        skipped: parsed.invalidRows.length,
        cancelled,
        total: parsed.totalRows,
        rows: outcomes,
      };
      setSummary(result);
      setPosting(false);
      setProgress(null);
      return result;
    },
    [],
  );

  return { post, cancel, reset, posting, progress, summary };
}
